import { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import SEOMeta from "@/components/SEOMeta";
import NavbarNew from "@/components/NavbarNew";
import FooterNew from "@/components/FooterNew";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, ExternalLink, Github, FolderOpen } from "lucide-react";
import { ConceptBadge } from "@/components/shared/ConceptBadge";
import { useProject } from "@/hooks/useProjects";
import { isConceptProject } from "@/lib/caseStudy";
import { safeHttpUrl } from "@/lib/safeUrl";

const ProjectDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: project, isLoading, isError } = useProject(id);

  useEffect(() => {
    if (
      !isLoading &&
      (isError || !project || project.status !== "published" || project.deleted_at)
    ) {
      navigate("/projetos", { replace: true });
    }
  }, [isLoading, isError, project, navigate]);

  if (isLoading || !project || project.status !== "published" || project.deleted_at) {
    return (
      <div className="min-h-screen bg-background">
        <NavbarNew />
        <main className="container mx-auto max-w-4xl px-4 pb-20 pt-10 sm:px-6 lg:px-8">
          <Skeleton className="mb-8 aspect-video w-full rounded-xl" />
          <Skeleton className="mb-4 h-10 w-2/3" />
          <Skeleton className="mb-2 h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </main>
        <FooterNew />
      </div>
    );
  }

  const cs = project.case_study;
  const concept = isConceptProject(project);
  const projectUrl = safeHttpUrl(project.project_url);
  const githubUrl = safeHttpUrl(project.github_url);

  return (
    <div className="min-h-screen bg-background">
      <SEOMeta
        title={`${project.title} | Cases | Eliveuton Dev`}
        description={project.description}
        canonicalUrl={`/projetos/${project.id}`}
      />
      <NavbarNew />

      <main className="pb-20 pt-10">
        <div className="container mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <Button variant="ghost" size="sm" asChild className="mb-6">
            <Link to="/projetos">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Voltar para cases
            </Link>
          </Button>

          <div className="mb-8 aspect-video overflow-hidden rounded-2xl bg-muted">
            {project.image_url ? (
              <img
                src={project.image_url}
                alt={project.title}
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <FolderOpen className="h-16 w-16 text-muted-foreground opacity-50" />
              </div>
            )}
          </div>

          <div className="mb-3 flex flex-wrap gap-2">
            {concept && <ConceptBadge shape="pill" />}
            {project.featured && (
              <Badge variant="outline" className="border-primary/20 bg-primary/10 text-primary">
                Destaque
              </Badge>
            )}
          </div>

          <h1 className="mb-4 text-3xl font-bold md:text-4xl">{project.title}</h1>

          {project.technologies && project.technologies.length > 0 && (
            <div className="mb-6 flex flex-wrap gap-2">
              {project.technologies.map((tech) => (
                <Badge key={tech} variant="outline" className="text-xs">
                  {tech}
                </Badge>
              ))}
            </div>
          )}

          <p className="mb-8 text-lg leading-relaxed text-muted-foreground">
            {project.description}
          </p>

          {project.case_study?.metric && (
            <div className="mb-8 flex items-baseline gap-3 rounded-2xl border border-border bg-card p-5">
              <span className="text-3xl font-bold tracking-tight text-primary">
                {project.case_study.metric}
              </span>
              {project.case_study.metricLabel && (
                <span className="text-sm text-muted-foreground">
                  {project.case_study.metricLabel}
                </span>
              )}
            </div>
          )}

          {concept && (
            <aside
              data-concept-notice
              className="mb-8 rounded-2xl border border-notice/30 bg-notice-soft p-5 text-[14px] leading-relaxed"
            >
              <strong className="font-bold">Projeto conceito.</strong> A empresa e a marca são
              fictícias, criadas para demonstrar o tipo de site que eu entrego: identidade visual,
              landing page responsiva, SEO e acessibilidade. Quer um assim para o seu negócio?{" "}
              <Link to="/contato" className="font-semibold text-primary underline">
                Fale comigo
              </Link>
              .
            </aside>
          )}

          {(projectUrl || githubUrl) && (
            <div className="mb-12 flex gap-3">
              {projectUrl && (
                <Button asChild>
                  <a href={projectUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="mr-2 h-4 w-4" />
                    {concept ? "Ver demo ao vivo" : "Ver projeto"}
                  </a>
                </Button>
              )}
              {githubUrl && (
                <Button variant="outline" asChild>
                  <a href={githubUrl} target="_blank" rel="noopener noreferrer">
                    <Github className="mr-2 h-4 w-4" />
                    Código
                  </a>
                </Button>
              )}
            </div>
          )}

          {cs && (cs.challenge || cs.solution || cs.results) && (
            <div className="space-y-10 border-t border-border pt-10">
              {cs.challenge && (
                <section>
                  <h2 className="mb-3 font-display text-xl font-bold">O desafio</h2>
                  <p className="leading-relaxed text-muted-foreground">{cs.challenge}</p>
                </section>
              )}
              {cs.solution && (
                <section>
                  <h2 className="mb-3 font-display text-xl font-bold">A solução</h2>
                  <p className="leading-relaxed text-muted-foreground">{cs.solution}</p>
                </section>
              )}
              {cs.results && (
                <section>
                  <h2 className="mb-3 font-display text-xl font-bold">Resultados</h2>
                  <p className="leading-relaxed text-muted-foreground">{cs.results}</p>
                </section>
              )}
              {cs.gallery && cs.gallery.length > 0 && (
                <section className="grid gap-4 sm:grid-cols-2">
                  {cs.gallery.map((src) => (
                    <img
                      key={src}
                      src={src}
                      alt={`Imagem do case ${project.title}`}
                      loading="lazy"
                      className="w-full rounded-lg object-cover"
                    />
                  ))}
                </section>
              )}
            </div>
          )}
        </div>
      </main>

      <FooterNew />
    </div>
  );
};

export default ProjectDetail;
